import { useState } from "react";
import { CalendarDays, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { inr, today, useLoad } from "@/lib/api";
import { ErrorBox, PageHeading } from "@/components/common";
import MonthlyReport from "@/components/MonthlyReport";

function shareText(r) {
  const c = r.close?.status === "closed" ? `Closed: counted ${inr(r.close.counted)} (${r.close.difference === 0 ? "matched" : `diff ${inr(r.close.difference)}`})` : "Drawer not closed yet";
  return [`${r.clinic.name} · Day-end ${r.day}`, `Collected ${inr(r.collections.payments)} (${r.collections.payment_count}) · Refunds ${inr(r.collections.refunds)} · Net ${inr(r.collections.net)}`,
    `Cash: opening ${inr(r.cash.opening)}, in ${inr(r.cash.cash_in)}, out ${inr(r.cash.cash_out)}, expected ${inr(r.cash.expected)}. ${c}`,
    `Visits ${r.visits} · New patients ${r.new_patients.length} · Expenses ${inr(r.expenses)} · Lab paid ${inr(r.lab_payments)}`,
    `Tomorrow: ${r.tomorrow_appointments.length} appointments${r.tomorrow_appointments.length ? ` (${r.tomorrow_appointments.map((a) => `${a.time || "any"} ${a.patient_name}`).join(", ")})` : ""}`].join("\n");
}

const Row = ({ l, v, tid, strong }) => <div className={strong ? "rep-row strong" : "rep-row"}><span>{l}</span><b data-testid={tid}>{v}</b></div>;

function DayEnd() {
  const [day, setDay] = useState(today());
  const { data: r, error } = useLoad(`/reports/day-end?day=${day}&tz_offset=${new Date().getTimezoneOffset()}`, null);
  const share = async () => {
    const text = shareText(r);
    if (navigator.share) { try { await navigator.share({ title: "Day-end report", text }); return; } catch { /* fall through */ } }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Opening WhatsApp with the summary");
  };
  return (
    <div className="content">
      <PageHeading eyebrow="REPORTS / DAY-END" title="The day," accent="on one page." sub="Collections, cash closing, new patients and tomorrow’s appointments.">
        <input type="date" className="date-input" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="report-day-input" />
        <button className="secondary" onClick={() => window.print()} disabled={!r} data-testid="print-report-button"><Printer size={15} /> Print</button>
        <button className="primary" onClick={share} disabled={!r} data-testid="share-report-button"><Share2 size={16} /> Share</button>
      </PageHeading>
      <ErrorBox error={error} testid="report-error" />
      {r && (
        <article className="report-sheet" data-testid="day-end-report">
          <header><div>{r.clinic.logo_data_url && <img className="rep-logo" src={r.clinic.logo_data_url} alt="" data-testid="report-logo" />}<div><h2>{r.clinic.name}</h2><p className="muted">{r.clinic.address}{r.clinic.phone && ` · ${r.clinic.phone}`}</p></div></div><div className="rep-date"><span>DAY-END REPORT</span><b>{new Date(`${r.day}T00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</b></div></header>
          <div className="rep-grid">
            <section><h3>Collections</h3>
              <Row l={`Payments received (${r.collections.payment_count})`} v={inr(r.collections.payments)} tid="rep-payments" />
              <Row l="Refunds" v={inr(r.collections.refunds)} tid="rep-refunds" />
              <Row l="Discounts given" v={inr(r.collections.discounts)} tid="rep-discounts" />
              <Row l="Net collection" v={inr(r.collections.net)} tid="rep-net" strong />
              <Row l="Total outstanding (all patients)" v={inr(r.total_outstanding)} tid="rep-outstanding" />
            </section>
            <section><h3>Cash drawer</h3>
              <Row l="Opening" v={inr(r.cash.opening)} tid="rep-opening" />
              <Row l="Cash in" v={inr(r.cash.cash_in)} tid="rep-cash-in" />
              <Row l="Cash out" v={inr(r.cash.cash_out)} tid="rep-cash-out" />
              <Row l="Expected" v={inr(r.cash.expected)} tid="rep-expected" strong />
              {r.close?.status === "closed"
                ? <><Row l={`Counted (closed by ${r.close.closed_by_name})`} v={inr(r.close.counted)} tid="rep-counted" /><Row l="Difference" v={inr(r.close.difference)} tid="rep-difference" /></>
                : <p className="rep-note" data-testid="rep-not-closed">The drawer has not been closed for this day.</p>}
            </section>
            <section><h3>Clinic activity</h3>
              <Row l="Clinical visits" v={r.visits} tid="rep-visits" />
              <Row l="Appointments today" v={`${r.appointments_today}${r.missed_today ? ` (${r.missed_today} missed)` : ""}`} tid="rep-appointments" />
              <Row l="Expenses" v={inr(r.expenses)} tid="rep-expenses" />
              <Row l="Lab payments" v={inr(r.lab_payments)} tid="rep-lab" />
            </section>
            <section><h3>New patients ({r.new_patients.length})</h3>
              {r.new_patients.length ? r.new_patients.map((p) => <div className="rep-row" key={p.id}><span>{p.name}</span><b>{p.opd}</b></div>) : <p className="rep-note">No new patients.</p>}
            </section>
          </div>
          <section className="rep-tomorrow"><h3><CalendarDays size={15} /> Tomorrow · {r.tomorrow} ({r.tomorrow_appointments.length})</h3>
            {r.tomorrow_appointments.length ? r.tomorrow_appointments.map((a) => <div className="rep-row" key={a.id} data-testid={`rep-tomorrow-${a.id}`}><span>{a.time || "Any time"} · {a.patient_name} ({a.opd})</span><b>{a.purpose}{a.reminded_at ? " · reminded" : ""}</b></div>) : <p className="rep-note">No appointments booked for tomorrow.</p>}
          </section>
        </article>
      )}
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("day");
  return (
    <>
      <div className="content report-tabs-wrap">
        <div className="tabs report-tabs">
          <button className={tab === "day" ? "tab active" : "tab"} onClick={() => setTab("day")} data-testid="report-tab-day">Day-end</button>
          <button className={tab === "monthly" ? "tab active" : "tab"} onClick={() => setTab("monthly")} data-testid="report-tab-monthly">Monthly & profit</button>
        </div>
      </div>
      {tab === "day" ? <DayEnd /> : <div className="content"><PageHeading eyebrow="REPORTS / MONTHLY" title="Month by month," accent="what's left." sub="Collections, expenses, lab costs and profit for your clinic." /><MonthlyReport /></div>}
    </>
  );
}
